// Plan type helpers shared by the exit plan form, the IA page, the hubs and the exemption page.
// An Exemption Request is an exit plan (same backend record) with planType = EXEMPTION_REQUEST
// plus the extra attributes handled by ExemptionFieldsSection.

export const PLAN_TYPES = {
  EXIT_PLAN: 'EXIT_PLAN',
  EXEMPTION_REQUEST: 'EXEMPTION_REQUEST',
};

export const PLAN_TYPE_LABELS = {
  [PLAN_TYPES.EXIT_PLAN]: 'Exit Plan',
  [PLAN_TYPES.EXEMPTION_REQUEST]: 'Exemption Request',
};

export const EXEMPTION_STATUS = {
  PENDING_LOCAL_RISK_ASSESSMENT: 'PENDING_LOCAL_RISK_ASSESSMENT',
};

// Statuses in which the RD may still edit / delete / submit the plan.
export const EDITABLE_PLAN_STATUSES = ['DRAFT', 'WAITING_FOR_SUPPORT', 'SUPPORT_PROVIDED', 'REJECTED'];

export const normalizePlanType = (planType) =>
  planType === PLAN_TYPES.EXEMPTION_REQUEST ? PLAN_TYPES.EXEMPTION_REQUEST : PLAN_TYPES.EXIT_PLAN;

const typeOf = (planOrType) => (typeof planOrType === 'string' ? planOrType : planOrType?.planType);

export const isExemption = (planOrType) => normalizePlanType(typeOf(planOrType)) === PLAN_TYPES.EXEMPTION_REQUEST;

export const getPlanTypeLabel = (planOrType) => PLAN_TYPE_LABELS[normalizePlanType(typeOf(planOrType))];

// Empty exemption-only fields, used to reset the form.
export const EMPTY_EXEMPTION_FIELDS = {
  toImpactEuros: '',
  exceptionStartDate: null,
  exceptionEndDate: null,
  justification: '',
  safetyAssessmentReference: '',
  productTypes: '',
  brands: '',
};

/**
 * Validates the exemption-only fields. Returns an errors object (empty when valid).
 * Required: exception start date, exception end date, justification.
 * Optional: TO impact (non-negative number when given), safety assessment reference.
 */
export const validateExemptionFields = ({
  toImpactEuros,
  exceptionStartDate,
  exceptionEndDate,
  justification,
}) => {
  const errors = {};
  if (!exceptionStartDate) errors.exceptionStartDate = 'Exception start date is required';
  if (!exceptionEndDate) errors.exceptionEndDate = 'Exception end date is required';
  if (exceptionStartDate && exceptionEndDate && new Date(exceptionEndDate) < new Date(exceptionStartDate)) {
    errors.exceptionEndDate = 'Exception end date must be on or after the start date';
  }
  if (!justification || !String(justification).trim()) errors.justification = 'Justification is required';
  if (toImpactEuros !== '' && toImpactEuros !== null && toImpactEuros !== undefined) {
    const amount = Number(toImpactEuros);
    if (Number.isNaN(amount) || amount < 0) errors.toImpactEuros = 'TO impact must be a non-negative number';
  }
  return errors;
};

/** Converts the form value of TO impact into the API value (number or null). */
export const toImpactForApi = (value) =>
  value === '' || value === null || value === undefined ? null : Number(value);

/** Human readable status text; SUBMITTED reads differently for exit plans and exemptions. */
export const getPlanStatusLabel = (status, planType) => {
  if (!status) return 'Unknown';
  if (status === 'SUBMITTED') {
    return isExemption(planType) ? 'Submitted – Pending Approvals' : 'Submitted (Final)';
  }
  if (status === EXEMPTION_STATUS.PENDING_LOCAL_RISK_ASSESSMENT) return 'Pending Local Risk Assessment';
  return status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};
